import React from 'react';
import { useAllContent, useContent } from '../hooks/useContent';
import { Award, Users, Clock, MapPin, Phone, Mail } from 'lucide-react';
import SEO from '../components/SEO';

const AboutPage = () => {
  const { contentData } = useAllContent();
  const { content: heroImages } = useContent('heroImages');
  
  const contactContent = contentData.contact || {
    office_phone: "01530 231 235",
    mobile_phone: "07718 738 785",
    email: "info@lawmancommercials.com",
    address: "31 Merrylees Industrial Estate, Leeside, Desford, Leicester LE9 9FS",
    company_name: "Lawman Commercials"
  };

  return (
    <div className="bg-white">
      <SEO 
        title="About Us"
        description="Lawman Commercials - 30+ years of MAN truck expertise in Leicester. Professional commercial vehicle sales, hire, repairs and parts. Export to 30+ countries worldwide. MANCAT3 diagnostics and genuine parts specialists."
        keywords="about Lawman Commercials, MAN truck specialists Leicester, commercial vehicle experts, truck sales company history"
        canonical="/about"
      />
      
      {/* Hero Section */}
      <section 
        className="bg-black text-white py-16 relative"
        style={{
          backgroundImage: `url('${heroImages?.aboutPage || 'https://customer-assets.emergentagent.com/job_radius-consistency/artifacts/4rdvvl9o_repairedTruck.webp'}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-black mb-6 font-inter">
              About <span className="text-orange-500">Lawman Commercials</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Your trusted commercial vehicle specialist since 1994. Over 30 years of experience serving the transport industry with excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-black text-black mb-6 font-inter">Our Story</h2>
              <div className="space-y-4 text-gray-700 font-inter">
                <p className="text-lg leading-relaxed">
                  Founded in 1994, Lawman Commercials is a family-run company keen on exceeding customer expectation and satisfaction. We are an honest company who is willing to apply our specialist knowledge on any size job no matter how big or small.
                </p>
                <p>
                  Our experienced Sales team is headed by the company director <strong>Lawson Williams</strong>. Lawson works alongside his staff in order to show hands-on approach to business. Team members at Lawman Commercials have adapted the Lawman approach of <em>"every little helps,"</em> meaning every individual knows and understands that each and every customer is important.
                </p>
                <p>
                  Even if you just need advice, we will always be here to help. This philosophy has guided us for over 30 years and continues to be at the heart of everything we do.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_easy-truck-sales/artifacts/99r070cz_example1.png"
                alt="Lawman Commercials facility"
                className="w-full h-96 object-cover rounded-lg shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter">
              Why Choose <span className="text-orange-600">Lawman Commercials</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Award className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-2 font-inter">30+</h3>
              <p className="text-gray-600 font-inter">Years of Experience</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Users className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-2 font-inter">Family</h3>
              <p className="text-gray-600 font-inter">Run Business</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Clock className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-2 font-inter">24/7</h3>
              <p className="text-gray-600 font-inter">Emergency Support</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-2xl font-bold">MAN</div>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2 font-inter">MAN</h3>
              <p className="text-gray-600 font-inter">Brand Specialist</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter">
              Our <span className="text-orange-600">Expertise</span>
            </h2>
            <p className="text-lg text-gray-600 font-inter max-w-2xl mx-auto">
              We are pleased to provide comprehensive commercial vehicle services to meet all your business needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Engine Specialists",
                description: "Expert diagnosis and repair of commercial vehicle engines with specialist knowledge and genuine parts."
              },
              {
                title: "Parts Specialists", 
                description: "Wide selection of new and used parts to suit all your commercial vehicle fleet needs."
              },
              {
                title: "Service & Diagnosis",
                description: "Comprehensive service and diagnostic capabilities using the latest equipment and techniques."
              },
              {
                title: "Vehicle Sales",
                description: "Quality used commercial vehicles with full service history and competitive pricing."
              },
              {
                title: "Vehicle Repairs",
                description: "Professional repair services for all commercial vehicle types and brands."
              },
              {
                title: "Hire Vehicles",
                description: "Flexible vehicle hire options for short-term and long-term requirements."
              },
              {
                title: "Emergency Callout",
                description: "24/7 emergency breakdown and repair services to keep your business moving."
              }
            ].map((service, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-black mb-3 font-inter">{service.title}</h3>
                <p className="text-gray-600 font-inter">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-black mb-4 font-inter">
              Our <span className="text-orange-600">Leadership</span>
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                <Users className="h-16 w-16 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-2 font-inter">Lawson Williams</h3>
              <p className="text-orange-600 font-bold mb-4">Company Director</p>
              <p className="text-gray-600 font-inter max-w-2xl mx-auto">
                Leading our experienced sales team with a hands-on approach to business. Lawson works alongside his staff to ensure every customer receives the personal attention and expertise they deserve.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="bg-orange-600 text-white rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4 font-inter">We're Hiring!</h3>
              <p className="text-lg mb-4">
                We are looking for a trained HGV Technician to join our ranks.
              </p>
              <p className="mb-6">
                Join our team and be part of a family business that values every employee and customer.
              </p>
              <a 
                href={`tel:${contactContent.office_phone?.replace(/\s/g, '')}`}
                className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-3 rounded font-bold inline-flex items-center gap-2 transition-colors"
              >
                <Phone className="h-5 w-5" />
                Call for More Info
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MAN Brand Specialist */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-black text-white rounded-lg p-12 text-center">
            <h2 className="text-3xl font-black mb-6 font-inter">
              Independent <span className="text-orange-500">MAN Brand</span> Specialist
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8 font-inter">
              We are honoured to be recognised as an independent MAN brand specialist. We stand at Convoy In The Park at Donnington Park alongside our loyal customers who have supported us over the years.
            </p>
            <p className="text-lg text-gray-400 font-inter">
              We would like to thank all our customers who have supported us over the past 30+ years for making this possible!
            </p>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black mb-6 font-inter">
            Ready to Experience the Lawman Difference?
          </h2>
          <p className="text-xl mb-8 font-inter opacity-90">
            Contact our expert team today. Even if you just need advice, we will always be here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${contactContent.office_phone?.replace(/\s/g, '')}`}
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded font-bold text-lg transition-colors inline-flex items-center justify-center"
            >
              <Phone className="mr-2 h-5 w-5" />
              {contactContent.office_phone}
            </a>
            <a 
              href={`mailto:${contactContent.email}`}
              className="bg-black hover:bg-gray-800 text-white px-8 py-4 rounded font-bold text-lg transition-colors inline-flex items-center justify-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Email Us
            </a>
          </div>
          <div className="mt-6 flex items-center justify-center text-lg">
            <MapPin className="mr-2 h-5 w-5" />
            {contactContent.address}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;